/**
 * "All | Unread only" above a list of things that can be read, with how many
 * are unread beside it — the notification inbox, messages.
 *
 * The count's wording is the page's (`countLabel`): "3 unread notifications"
 * and "3 unread" are different sentences, and the noun belongs to the screen.
 * Hidden at zero, where it would only say "0 unread".
 */
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

export interface UnreadFilterProps {
  unreadOnly: boolean;
  onChange: (unreadOnly: boolean) => void;
  unreadCount: number;
  /** The translated count sentence, e.g. `t('inbox:unreadCount', { count })`. */
  countLabel: string;
}

export function UnreadFilter({ unreadOnly, onChange, unreadCount, countLabel }: UnreadFilterProps) {
  const { t } = useTranslation('common');

  return (
    <Stack direction="row" sx={{ mb: 2, alignItems: 'center', gap: 2 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={unreadOnly ? 'unread' : 'all'}
        onChange={(_event, value: 'all' | 'unread' | null) => {
          // Pressing the selected button again deselects it; that is not a third state.
          if (value !== null) onChange(value === 'unread');
        }}
      >
        <ToggleButton value="all">{t('unread.showAll')}</ToggleButton>
        <ToggleButton value="unread">{t('unread.showUnreadOnly')}</ToggleButton>
      </ToggleButtonGroup>
      {unreadCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          {countLabel}
        </Typography>
      )}
    </Stack>
  );
}
